'use client';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { trackCustomEvent } from '@/lib/analytics';
import { waitForGoogletagLoaded } from './helpers';
import HouseBanner, { isOnScreen } from './HouseBanner';
// import { useScreenWidthLimit } from '@/hooks/useScreenWidthLimit';

interface ImageProps {
  card?: any;
  index?: number;
  identifier: string;
  onAdLoaded?: (loaded: boolean) => void; // Optional callback when ad loads/fails
}

const AdComponent: React.FC<ImageProps> = (props) => {
  // const { forceHide } = useScreenWidthLimit(728); // Use the custom hook to determine visibility

  const cardElementRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const [gaEvent, setGaEvent] = useState(false);
  const [googleTagLoaded, setGoogleTagLoaded] = useState(false);
  // No-fill handling, final design (Aug 2026 Lighthouse audit follow-up):
  // - The ad request now fires ~1200px BEFORE the slot reaches the
  //   viewport (see the eager observer below), so a no-fill verdict
  //   almost always arrives while the slot is still off screen — it
  //   collapses before the user ever sees it: no white band, and no
  //   visible layout shift (CLS only counts movement of visible content).
  // - A slot that's visible at page load gets its verdict while on
  //   screen; it collapses immediately — one small shift ~1s after load
  //   beats a band of dead whitespace parked at the top of the page
  //   (tried holding the space until scroll-out: rejected in testing,
  //   every slot the user looked at showed an empty band).
  // - When an ad DOES fill, the reserved min-height means zero shift —
  //   which is the case that actually dominates production traffic.
  const [collapsed, setCollapsed] = useState(false);
  // Oct 2026 revision: collapsing a slot that is ON screen turned out to be
  // a real field problem (Search Console: home CLS 0.20, desktop tag /
  // district 0.11-0.17, starting the week this shipped). An on-screen
  // no-fill now keeps its box and shows our own banner in it; only
  // off-screen slots collapse.
  const [houseBanner, setHouseBanner] = useState(false);
  const requestCollapse = () => {
    if (isOnScreen(cardElementRef.current)) setHouseBanner(true);
    else setCollapsed(true);
  };

  // 1. CRITICAL FIX: Use a Ref instead of State for adShown. 
  // This prevents stale closures in setTimeout from causing double-refreshes.
  const adShownRef = useRef(false); 
  const reactId = useId();
  // useId() alone can repeat across SPA route transitions.
  // Add a client-only random suffix so GPT slot IDs stay unique per mount.
  const [uniqueId, setUniqueId] = useState('');
  useEffect(() => {
    setUniqueId(Math.random().toString(36).substring(2, 8));
  }, []);
  const adDivId = useMemo(() => {
    const sanitizedReactId = reactId.replace(/[^a-zA-Z0-9_-]/g, '');
    const sanitizedIdentifier = props.identifier.replace(/[^a-zA-Z0-9_-]/g, '-');
    const safeIndex = props.index ?? 'na';
    const suffix = uniqueId ? `-${uniqueId}` : '';
    return `div-gpt-ad-dweb-nextVideo-${sanitizedIdentifier}-${safeIndex}-${sanitizedReactId}${suffix}`;
  }, [props.identifier, props.index, reactId, uniqueId]);
  const targetSlotRef = useRef<any>(null);

  // Eager observer: fires the ad request ~1200px before the slot is
  // visible, so GPT's fill/no-fill verdict lands while the slot is still
  // off screen (see the no-fill design note above). Display-trigger only —
  // viewability analytics keeps its own strict observer below.
  const { ref: eagerRef } = useInView({
    triggerOnce: false,
    threshold: 0,
    rootMargin: '1200px 0px',
    onChange: (nearView: boolean) => {
      if (nearView && !adShownRef.current && targetSlotRef.current) {
        if (window.googletag) {
          adShownRef.current = true; // Lock immediately
          window.googletag.cmd.push(function () {
            // FIX: removed refresh to avoid GPT blocking
            window.googletag.display(adDivId);
            window.googletag.pubads().refresh([targetSlotRef.current]);
          });
        }
      }
    },
  });

  const { ref: inViewRef } = useInView({
    triggerOnce: false,
    threshold: 0.1,
    onChange: (inView: boolean) => {
      if (inView && !gaEvent) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
          setGaEvent(true);
          if (cardElementRef.current && cardElementRef.current.id) {
            trackCustomEvent('DWEB_AD_VIEW', {
              category: 'DWEB_AD_VIEW',
              action: 'pageview',
              label: cardElementRef.current.id,
              customData: { position: props.index },
            });
          }
        }, 500);
      }
    },
  });

  useEffect(() => {
    let toUpdate = true;
    waitForGoogletagLoaded()
    .then(() => {
      if (toUpdate) setGoogleTagLoaded(true);
    })
      .catch((e) => {
        console.error(e);
        // googletag never loaded (ad blocker, script failure) — no ad is
        // ever coming, so treat it like a no-fill.
        if (toUpdate) requestCollapse();
      });
    return () => { 
      toUpdate = false;
     };
  }, []);

  const checkAndDisplayAd = () => {
    // Check the Ref here as well
    if (!targetSlotRef.current || adShownRef.current || !window.googletag) return;
    
    const adElement = document.getElementById(adDivId);
    if (adElement) {
      const rect = adElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      // Check if element is visible or within viewport (with generous buffer)
      const isVisibleOrNear = 
        (rect.top >= -1200 && rect.top < viewportHeight + 1200) ||
        (rect.bottom > -1200 && rect.bottom <= viewportHeight + 1200) ||
        // Also check if element is in viewport horizontally
        (rect.left >= -500 && rect.left < viewportWidth + 500);
      
      // Check if element is hidden (for modal ads)
      const computedStyle = window.getComputedStyle(adElement);
      const isHidden = 
        adElement.style.visibility === 'hidden' ||
        adElement.style.display === 'none' ||
        computedStyle.visibility === 'hidden' ||
        computedStyle.display === 'none' ||
        adElement.closest('[style*="visibility: hidden"]') !== null ||
        (Math.abs(rect.left) > 10000 || Math.abs(rect.top) > 10000);
      
      if (isVisibleOrNear || isHidden) {
        adShownRef.current = true; // Lock immediately
        window.googletag.cmd.push(function () {
          window.googletag.display(adDivId);
          window.googletag.pubads().refresh([targetSlotRef.current]);
        });
      }
    }
  };

  useEffect(() => {
    if (!googleTagLoaded || !uniqueId) return;
    let isMounted = true;

    if (window.googletag) {
      window.googletag.cmd.push(function () {
        if (!isMounted) return;
        if (!window.isGptInitialized) {
          window.googletag.pubads().disableInitialLoad();
          window.googletag.enableServices();
          // @ts-ignore
          window.isGptInitialized = true;
        }
        const existingSlots = window.googletag.pubads().getSlots();
        const existing = existingSlots.find(
          (s: any) => s.getSlotElementId() === adDivId,
        );
        if (existing) {
          window.googletag.destroySlots([existing]);
        }
        // Define a slot tied to this exact DOM id.
        targetSlotRef.current = window.googletag
          .defineSlot(
            `/22099787619/${props.identifier}`,
            [[1, 1], [300, 250], 'fluid'],
            adDivId,
          )
          .addService(window.googletag.pubads());
        // Trigger one initial visibility check so above-the-fold slots do not wait for scroll.
        setTimeout(() => {
          if (isMounted) checkAndDisplayAd();
        }, 300);
      });
    }

    // 2. ISOLATE LISTENER: Only hide if THIS specific ad returns empty
    const onRenderEnded = function (event: any) {
      const slotId = event.slot.getSlotElementId();
      if (slotId === adDivId) {
        if (event.isEmpty) {
          // No-fill: don't yank visible content (that was the measured
          // CLS 0.40-0.47) — requestCollapse() frees the space
          // immediately if the slot is off screen, or defers the
          // collapse until it scrolls out of view.
          requestCollapse();
          if (props.onAdLoaded) props.onAdLoaded(false);
        } else {
          if (props.onAdLoaded) props.onAdLoaded(true);
        }
      }
    };

    if (window.googletag) {
      window.googletag.cmd.push(function () {
        window.googletag
        .pubads()
        .addEventListener('slotRenderEnded', onRenderEnded);
      });
    }

    // 3. PROPER CLEANUP
    return () => {
      isMounted = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (window.googletag) {
        window.googletag.cmd.push(function () {
          // Remove listener to prevent memory leaks across routes
          window.googletag.pubads().removeEventListener('slotRenderEnded', onRenderEnded);
          if (targetSlotRef.current) {
            window.googletag.destroySlots([targetSlotRef.current]);
          }
        });
      }
    };
    // eslint-disable-next-line
  }, [googleTagLoaded, adDivId, uniqueId]);

  if (collapsed) {
    // With eager requests (~1200px ahead), no-fill verdicts almost always
    // arrive while the slot is still off screen — so this unmount is
    // usually invisible. See the design note at the top of the component.
    return null;
  }

  if (houseBanner) {
    return (
      <div className='flex justify-center items-center relative'>
        <div style={{ minWidth: 300, minHeight: 250, margin: '16px 0px' }}>
          <HouseBanner variant='box' />
        </div>
      </div>
    );
  }

  return (
    <div className='flex justify-center items-center relative'>
      <div
        ref={(el) => {
          inViewRef(el);
          eagerRef(el);
          cardElementRef.current = el;
        }}
        id={adDivId}
        style={{
          // Sized to the slot's dominant creative (300x250) — the 1x1 and
          // 'fluid' sizes in the requested sizes array are fallbacks, not
          // the common case. Reserving only 100px here (the old value) let
          // the actual 250px-tall creative pop in after load and shift
          // everything below it — this is the CLS source flagged in the
          // Aug 2026 Lighthouse audit (score 0.279, target <0.1).
          minWidth: 300,
          minHeight: 250,
          textAlign: 'center',
          margin: '16px 0px',
          position: 'relative',
        }}
      ></div>
    </div>
  );
};

export default AdComponent;
